import type { StockAnalysis } from "../../../../../api/services/stock-analysis.type";

/** Parse P/E from API (string or number); supports comma as decimal separator when no dot is present. */
export const parsePeRatioValue = (
  pe: string | number | undefined | null,
): number | null => {
  if (pe === undefined || pe === null) return null;
  if (typeof pe === "number") {
    return Number.isFinite(pe) ? pe : null;
  }
  const s = String(pe).trim();
  if (!s || s === "-") return null;
  let normalized = s.replace(/\s/g, "");
  if (normalized.includes(",") && !normalized.includes(".")) {
    normalized = normalized.replace(",", ".");
  } else {
    normalized = normalized.replace(/,/g, "");
  }
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
};

/** Display P/E with two fractional digits and a period as decimal separator (e.g. 5.20, -1.75). */
export const formatPeRatioForTable = (
  pe: string | number | undefined | null,
): string => {
  const n = parsePeRatioValue(pe);
  if (n !== null) return n.toFixed(2);
  if (pe === undefined || pe === null) return "-";
  const raw = String(pe).trim();
  return raw || "-";
};

// Helper function to parse numeric values for sorting
export const parseNumericValue = (
  value: string | number | undefined,
): number => {
  if (typeof value === "number") return value;
  if (!value || value === "-") return 0;

  // Remove currency symbols, commas, and common suffixes (T, B, M, K)
  const cleaned = String(value)
    .replace(/[$,\s]/g, "")
    .replace(/[Tt]rillion?/i, "000000000000")
    .replace(/[Bb]illion?/i, "000000000")
    .replace(/[Mm]illion?/i, "000000")
    .replace(/[Kk]?/i, "");

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

export type ExtendedStockAnalysis = StockAnalysis & {
  key: string;
  symbol?: string;
  companyName?: string;
  country?: string;
  marketCap?: string | number;
  pe?: string | number;
  price?: string | number;
  targetConsensus?: string | number;
  change?: string | number;
  consensus?: string;
  grade?: string;
  ranking?: string | number;
  halal_status?: string;
  logoURL?: string;
};

// Sorters
export const sortByMarketCap = (
  a: ExtendedStockAnalysis,
  b: ExtendedStockAnalysis,
): number => {
  const aValue = parseNumericValue(a.marketCap || a.market_cap);
  const bValue = parseNumericValue(b.marketCap || b.market_cap);
  return aValue - bValue;
};

export const sortByPrice = (
  a: ExtendedStockAnalysis,
  b: ExtendedStockAnalysis,
): number => {
  const aValue = parseNumericValue(a.price);
  const bValue = parseNumericValue(b.price);
  return aValue - bValue;
};

export const sortByRating = (
  a: ExtendedStockAnalysis,
  b: ExtendedStockAnalysis,
): number => {
  const aRating = a.consensus || a.grade || a.signal || "";
  const bRating = b.consensus || b.grade || b.signal || "";
  return aRating.localeCompare(bRating);
};

export const sortByRanking = (
  a: ExtendedStockAnalysis,
  b: ExtendedStockAnalysis,
): number => {
  const aValue = parseNumericValue(a.ranking);
  const bValue = parseNumericValue(b.ranking);
  return aValue - bValue;
};

/**
 * Copies a URL to clipboard with success/error notifications
 * @param url - The URL to copy (defaults to current page URL)
 * @param messageApi - Ant Design message API instance
 * @param successMessage - Custom success message (default: "Link copied to clipboard!")
 * @param errorMessage - Custom error message (default: "Failed to copy link")
 */
export const copyLinkToClipboard = async (
  messageApi: {
    success: (content: string) => void;
    error: (content: string) => void;
  },
  url?: string,
  successMessage: string = "Link copied to clipboard!",
  errorMessage: string = "Failed to copy link",
): Promise<void> => {
  try {
    const urlToCopy = url || window.location.href;

    // Use modern Clipboard API if available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(urlToCopy);
      messageApi.success(successMessage);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = urlToCopy;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      messageApi.success(successMessage);
    }
  } catch (error) {
    messageApi.error(errorMessage);
    console.error("Failed to copy link:", error);
  }
};
